import React, { useCallback, useEffect, useState, useRef } from "react";
import { Check, Trash2, Search, Copy, X } from "lucide-react";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import {
  getVehicles,
  updateVehicle,
  createVehicle,
  clearError,
  deleteVehicle,
} from "../../store/slices/vehiclesSlice";
import { withVersion } from "../../utils/concurrency";
import {
  CreateVehicle,
  Vehicle,
  UpdateVehicle,
  VehicleFilters as Filters,
} from "../../types/vehicles";
import Button from "../../components/UI/Button";
import { Modal } from "../../components/UI/Modal";
import { VehicleForm } from "../../components/vehicles/VehicleForm";
import { VehicleTable } from "../../components/vehicles/VehiclesTable";
import { Pagination } from "../../components/ListView/Pagination";
import { Input } from "../../components/UI/FormField";
import { useTranslation } from "react-i18next";
import { deviceAPI } from "../../services/api";
import type { TruckDevice } from "../../types/devices";
import { vehicleLabel } from "../../utils/vehicleLabel";

/** A pairing code on screen, however it got there — freshly issued or read back. */
interface ShownCode {
  code: string;
  plate: string;
  /** Newly issued, so the previous one (if any) has just stopped working. */
  fresh: boolean;
  expired?: boolean;
}

export const VehiclesListing: React.FC = () => {
  const { t } = useTranslation("vehicles");
  const dispatch = useAppDispatch();
  const {
    vehicles,
    loading,
    error,
    pagination: { total, page, pages },
  } = useAppSelector((state) => state.vehicles);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);

  /**
   * ── Truck devices, keyed by the vehicle they belong to ─────────────────────
   *
   * Loaded once for the page and joined onto the rows, rather than living on a
   * page of their own. A device *is* a property of a truck — one screen per cab,
   * showing that truck's containers — and splitting them across two screens made
   * an admin hold both in their head to answer "does ABC-123 have a screen, and
   * who is on it".
   *
   * Fetched separately from the vehicles list because it is a different resource
   * with a different permission, and because a failure here must not take the
   * vehicles table down with it: a dispatcher who cannot load devices should
   * still see their fleet.
   */
  const [devices, setDevices] = useState<Record<string, TruckDevice>>({});
  const [deviceBusy, setDeviceBusy] = useState(false);
  const [shownCode, setShownCode] = useState<ShownCode | null>(null);

  const loadDevices = useCallback(async () => {
    try {
      const res = await deviceAPI.list();
      const byVehicle: Record<string, TruckDevice> = {};
      for (const device of res.data.data.devices ?? []) {
        if (device.vehicleId?._id) byVehicle[device.vehicleId._id] = device;
      }
      setDevices(byVehicle);
    } catch {
      // Silent: the fleet is the page, devices are an extra column on it. An
      // error toast here would fire on every visit for a role that cannot read
      // devices, about something they did not ask for.
    }
  }, []);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  /** Register a screen against a truck and issue its first pairing code. */
  const handleAddDevice = async (vehicle: Vehicle) => {
    setDeviceBusy(true);
    try {
      const res = await deviceAPI.create({ vehicleId: vehicle._id });
      setShownCode({
        code: res.data.data.pairingCode,
        plate: vehicleLabel(vehicle),
        fresh: true,
      });
      await loadDevices();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t("device.addFailed"));
    } finally {
      setDeviceBusy(false);
    }
  };

  /**
   * Read back the code already outstanding.
   *
   * The first thing to reach for, and the reason it exists: regenerating for a
   * device already in service takes that truck's screen out of use until the new
   * code is redeemed. Somebody who merely closed the dialog too early should not
   * pay that.
   */
  const handleShowCode = async (device: TruckDevice) => {
    setDeviceBusy(true);
    try {
      const res = await deviceAPI.getPairingCode(device._id);
      const { pairingCode, expired } = res.data.data;
      setShownCode({
        code: pairingCode,
        plate: vehicleLabel(device.vehicleId),
        fresh: false,
        expired,
      });
    } catch (error: any) {
      // The server separates "already paired", "viewing not enabled" and "this
      // code predates viewing", each with its own next step — shown as-is.
      toast.error(error.response?.data?.message || t("device.showCodeFailed"));
    } finally {
      setDeviceBusy(false);
    }
  };

  const handleNewCode = async (device: TruckDevice) => {
    if (!window.confirm(t("device.confirmNewCode"))) return;

    setDeviceBusy(true);
    try {
      const res = await deviceAPI.regeneratePairingCode(device._id);
      setShownCode({
        code: res.data.data.pairingCode,
        plate: vehicleLabel(device.vehicleId),
        fresh: true,
      });
      await loadDevices();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t("device.newCodeFailed"));
    } finally {
      setDeviceBusy(false);
    }
  };

  const handleDeviceSignOut = async (device: TruckDevice) => {
    setDeviceBusy(true);
    try {
      await deviceAPI.signOut(device._id);
      toast.success(t("device.signedOut"));
      await loadDevices();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t("device.signOutFailed"));
    } finally {
      setDeviceBusy(false);
    }
  };

  const handleRevokeDevice = async (device: TruckDevice) => {
    if (!window.confirm(t("device.confirmRevoke"))) return;

    setDeviceBusy(true);
    try {
      await deviceAPI.revoke(device._id);
      toast.success(t("device.revoked"));
      await loadDevices();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t("device.revokeFailed"));
    } finally {
      setDeviceBusy(false);
    }
  };
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [filters, setFilters] = useState<Filters>({
    search: "",
    page: 1,
    limit: 20,
  });

  const prevSearch = useRef(filters.search);

  // Load Vehicles
  useEffect(() => {
    const searchChanged = prevSearch.current !== filters.search;
    prevSearch.current = filters.search;

    const params = {
      search: filters.search,
      page: filters.page,
      limit: filters.limit,
    };

    if (searchChanged) {
      if (filters.search === "") {
        dispatch(getVehicles(params));
      } else {
        const timer = setTimeout(() => dispatch(getVehicles(params)), 750);
        return () => clearTimeout(timer);
      }
      return;
    }
    dispatch(getVehicles(params));
  }, [dispatch, filters]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Filter changes
  const handleFiltersChange = (newFilters: Partial<Filters>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
      page: newFilters.page || 1,
    }));
    setSelectedIds([]);
  };

  // Pagination
  const handlePageChange = (page: number) => {
    handleFiltersChange({ page });
  };

  // Selection
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(vehicles.map((vehicle) => vehicle._id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedIds((prev) => [...prev, id]);
    } else {
      setSelectedIds((prev) => prev.filter((selectedId) => selectedId !== id));
    }
  };

  // CRUD Handlers
  const handleCreateVehicle = async (data: CreateVehicle) => {
    try {
      await dispatch(createVehicle(data)).unwrap();
      toast.success(t("toasts.createSuccess"));
      setIsModalOpen(false);
    } catch (error) {
      // A refusal here is usually a duplicate — the plate, or a display name
      // another truck already answers to — and the server says which. Swallowed,
      // it left the form open with no reason given.
      toast.error(typeof error === "string" ? error : t("toasts.createError"));
    }
  };

  const handleUpdateVehicle = async (data: Partial<Vehicle>) => {
    if (!editingVehicle) return;

    try {
      if (!data.registrationNumber || !data.type) {
        toast.error(t("toasts.validationError"));
        return;
      }

      // Map Vehicle -> UpdateVehicle — payload keys intentionally unchanged
      const vehicleInfo: UpdateVehicle = {
        registrationNumber: data.registrationNumber,
        type: data.type,
        displayName: data.displayName,
        markAndModel: data.markAndModel,
        additionalNotes: data.additionalNotes,
        weightCapacity: data.weightCapacity,
        numberOfAxles: data.numberOfAxles,
      };

      await dispatch(
        updateVehicle({
          id: editingVehicle._id,
          vehicleData: withVersion(vehicleInfo, editingVehicle),
        }),
      ).unwrap();

      toast.success(t("toasts.updateSuccess"));
      setIsModalOpen(false);
      setEditingVehicle(null);
    } catch (error) {
      console.error(error);
      toast.error(typeof error === "string" ? error : t("toasts.updateError"));
    }
  };

  const handleEdit = (vehicle: Vehicle) => {
    setEditingVehicle(vehicle);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(t("confirm.delete"))) {
      try {
        await dispatch(deleteVehicle(id)).unwrap();
        toast.success(t("toasts.deleteSuccess"));
        setSelectedIds((prev) =>
          prev.filter((selectedId) => selectedId !== id),
        );
      } catch (error) {}
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingVehicle(null);
  };

  const pagination = {
    page,
    pages,
    total,
    hasNext: page < pages,
    hasPrev: page > 1,
  };

  return (
    <div>
      {/* Header */}
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-dark-900 mb-4">
            {t("title")}
          </h1>
          <div className="flex items-center justify-between gap-4 max-sm:flex-wrap">
            {/* Search Bar - Left Side */}
            <div className="flex-1 min-w-[250px] max-w-[250px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder={t("search.placeholder")}
                  value={filters.search}
                  onChange={(e) =>
                    handleFiltersChange({ search: e.target.value })
                  }
                  className="pl-10 bg-white"
                />
              </div>
            </div>

            {/* Add Button - Right Side */}
            <div className="flex items-center space-x-3">
              <Button onClick={() => setIsModalOpen(true)} variant="primary">
                {t("actions.addVehicle")}
              </Button>
            </div>
          </div>
        </div>

        {/* Selected Items Bar */}
        {selectedIds.length > 0 && (
          <div className="mb-4 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Check className="h-5 w-5 text-blue-600" />
              <span className="text-blue-800 font-medium">
                {t("selection.itemsSelected", { count: selectedIds.length })}
              </span>
            </div>
            <button
              // onClick={handleBulkDelete}
              className="inline-flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <Trash2 className="h-4 w-4" />
              <span>{t("actions.deleteSelected")}</span>
            </button>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <VehicleTable
            vehicles={vehicles}
            selectedIds={selectedIds}
            onSelectAll={handleSelectAll}
            onSelectOne={handleSelectOne}
            onEdit={handleEdit}
            onDelete={handleDelete}
            loading={loading}
            devices={devices}
            onAddDevice={handleAddDevice}
            onShowCode={handleShowCode}
            onNewCode={handleNewCode}
            onSignOut={handleDeviceSignOut}
            onRevokeDevice={handleRevokeDevice}
          />
          {/* Pagination */}
          <Pagination
            pagination={pagination}
            onPageChange={handlePageChange}
            pageSize={filters.limit}
          />
        </div>
      </div>

      {/* ── A pairing code on screen ────────────────────────────────────────
          One dialog for both ways of getting here — freshly issued, or read
          back — because what the admin does next is identical: read it out or
          carry it to the tablet. Only the warning differs, and it differs
          because a fresh code has just retired the previous one. */}
      {shownCode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 text-center shadow-xl">
            <div className="mb-1 flex items-start justify-between">
              <span />
              <button
                type="button"
                onClick={() => setShownCode(null)}
                className="rounded-lg p-1 text-gray-400 hover:bg-gray-100"
                aria-label={t("device.code.close")}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <h2 className="font-semibold text-gray-900">
              {t("device.code.title", { plate: shownCode.plate })}
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {t("device.code.subtitle")}
            </p>

            <p className="my-6 select-all font-mono text-4xl font-bold tracking-[0.2em] text-gray-900">
              {shownCode.code}
            </p>

            {/* An expired code is still shown — see the controller. Knowing what
                was issued, and that it has lapsed, explains a tablet refusing it
                far better than a bare "expired" with nothing to compare. */}
            {shownCode.expired && (
              <p className="mb-3 rounded-xl bg-red-50 px-3 py-2 text-xs text-red-800">
                {t("device.code.expired")}
              </p>
            )}

            <p className="mb-5 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-900">
              {shownCode.fresh
                ? t("device.code.warnFresh")
                : t("device.code.warnShown")}
            </p>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(shownCode.code);
                    toast.success(t("device.code.copied"));
                  } catch {
                    // Refused on insecure origins and locked-down browsers. The
                    // code is on screen regardless.
                    toast.info(t("device.code.copyManually"));
                  }
                }}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-gray-200 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50"
              >
                <Copy className="h-4 w-4" />
                {t("device.code.copy")}
              </button>
              <button
                type="button"
                disabled={deviceBusy}
                onClick={() => setShownCode(null)}
                className="flex-1 rounded-xl bg-gray-900 py-2.5 text-sm font-medium text-white disabled:opacity-50"
              >
                {t("device.code.close")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      <div className="flex-1 overflow-y-auto">
        <Modal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title={
            editingVehicle
              ? t("modal.editTitle", {
                  id: editingVehicle._id.slice(-6).toUpperCase(),
                })
              : t("modal.createTitle")
          }
          size="lg"
        >
          <div className="flex-1 overflow-y-auto">
            <VehicleForm
              vehicle={editingVehicle || undefined}
              onSubmit={(data) => {
                if (editingVehicle) {
                  return handleUpdateVehicle(data as any);
                } else {
                  return handleCreateVehicle(data as CreateVehicle);
                }
              }}
              loading={loading}
              isEditMode={!!editingVehicle}
            />
          </div>
        </Modal>
      </div>
    </div>
  );
};
